import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import { SegmentDefinition } from 'domains/segments/types';
import QueryKeys from 'hooks/queries/keys';
import { ApiMethods, CuredApiPaths } from 'models/enums';

import useMutation from '../useMutation';

import MutationKeys from './keys';

/**
 * Manage Display Conditions table row shape. Distinct from `RowDisplayCondition` (the Bee
 * editor's condition-picker shape) — this carries archive state, timestamps, and the raw
 * filter definition (needed to hydrate the edit UI) for the Manage table's columns.
 */
export type DisplayConditionListItem = {
    id: number;
    label: string;
    description: string;
    category: string;
    isArchived: boolean;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    condition: SegmentDefinition;
};

/**
 * Archives a display condition (soft-delete). The row stays in the DB but is excluded from
 * the Bee editor's condition picker and the Manage Display Conditions active list.
 * @param conditionId the id of the condition being archived
 * @param mutateOptions react-query useMutation option
 */
const useArchiveDisplayCondition = (
    conditionId: number,
    mutateOptions?: UseMutationOptions<DisplayConditionListItem, Error, void>
) => {
    const queryClient = useQueryClient();
    const { onSuccess: onSuccessOption, ...restMutateOptions } = mutateOptions ?? {};

    return useMutation<void, DisplayConditionListItem>(
        MutationKeys.ARCHIVE_DISPLAY_CONDITION(conditionId),
        `${CuredApiPaths.ROW_DISPLAY_CONDITIONS}/${conditionId}/archive`,
        {
            onSuccess: async (data, variables, context) => {
                // Await so the Manage table's cache is already fresh by the time this resolves.
                await Promise.all([
                    queryClient.invalidateQueries(QueryKeys.ROW_DISPLAY_CONDITIONS),
                    queryClient.invalidateQueries(QueryKeys.DYNAMIC_CONTENT_CATEGORIES),
                    queryClient.invalidateQueries(['manage-display-conditions']),
                ]);
                onSuccessOption?.(data, variables, context);
            },
            ...restMutateOptions,
        },
        ApiMethods.POST
    );
};

export default useArchiveDisplayCondition;
